import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ASSETS, formatApiError } from "@/lib/api";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send reset email");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--trio-bg)] text-black flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-md bg-white border border-black/10 shadow-xl rounded-lg p-8">
        <div className="text-center mb-6">
          <img src={ASSETS.logo} alt="Trio" className="h-14 w-auto mx-auto mb-4" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
            Forgot Your Password?
          </div>
        </div>

        {sent ? (
          <div className="text-center space-y-4" data-testid="forgot-sent">
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
            <p className="text-sm text-black leading-relaxed">
              If an account exists for <b>{email}</b>, a reset link is on the way. Check your inbox (and spam folder) — it expires in 1 hour.
            </p>
            <Link
              to="/staff/login"
              className="inline-flex items-center gap-2 border border-black/25 hover:border-[var(--trio-primary)] px-4 py-2 text-xs tracking-widest uppercase font-bold"
            >
              Back to Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
            <p className="text-sm text-black/70">
              Type your email and we'll send you a link to set a new password. Works for staff, admin, or owner accounts.
            </p>
            <div>
              <label className="text-xs tracking-widest uppercase text-black/70 font-semibold">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email"
                className="w-full mt-1 bg-white border border-black/25 focus:border-[var(--trio-primary)] px-4 py-3 text-black focus:outline-none rounded"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="forgot-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white py-3 text-sm tracking-widest uppercase font-bold disabled:opacity-60 rounded"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Send Reset Link
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link to="/staff/login" className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-black/40 hover:text-black/70">
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
